import { useCallback, useEffect, useRef, useState } from "react";
import ProductTable from "./ProductTable";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  useFetchOrderDetailsData,
  useFetchRouteOrdersData,
} from "../../../apis/orderApis";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";
import { IconScan } from "@tabler/icons-react";

export const SystemTypes = [
  { value: "phone", text: "Phone" },
  { value: "email", text: "Email" },
  { value: "whatsapp", text: "WhatsApp" },
];

type Props = {
  routeId: string;
  drsId?: string;
  mode: string;
  setMode: React.Dispatch<React.SetStateAction<string>>;
  selectedOrders: string[];
  warehouseId: string;
  setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>;
};

// ---- Audio feedback helper ----
const playBeep = () => {
  const Ctx =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctx) return;
  const context = new Ctx();
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(context.destination);
  oscillator.frequency.value = 440;
  gainNode.gain.setValueAtTime(0, context.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.5, context.currentTime + 0.05);
  oscillator.start(context.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(
    0.00001,
    context.currentTime + 0.2
  );
  oscillator.stop(context.currentTime + 0.2);
};

const AddShipmentIdWise = (props: Props) => {
  const { routeId, drsId, mode, setMode, selectedOrders, setSelectedOrders, warehouseId } =
    props;
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<any>("");
  // scanning
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const lastScanTime = useRef<number>(0);
  const [orderIdToFetch, setOrderIdToFetch] = useState<string | null>(null);

  const { data: orders, refetch } = useFetchRouteOrdersData(
    mode ? pageNo : 0,
    mode ? searchFor : "",
    mode ? routeId : "",
    "create",
    "",
    mode ? warehouseId : "",
  );

  useEffect(() => {
    refetch();
  }, [])

  const { data: fetchedOrderDetails, isFetching: isFetchingDetails } =
    useFetchOrderDetailsData(orderIdToFetch ?? "");
  const [scannedOrderDetails, setScannedOrderDetails] = useState<any[]>([]);
  const [pasteData, setPasteData] = useState("");

  useEffect(() => {
    if (fetchedOrderDetails && orderIdToFetch) {
      setScannedOrderDetails((prev) => [...prev, fetchedOrderDetails]);
      setOrderIdToFetch(null);
    }
  }, [fetchedOrderDetails, orderIdToFetch]);

  // ---- Scanner controls (guarded by canProceed) ----
  const stopScanner = useCallback(() => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    setIsScanning(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (!mode) return; // ✅ guard
    if (!videoRef.current) return;
    try {
      if (!codeReader.current)
        codeReader.current = new BrowserMultiFormatReader();
      setIsScanning(true);
      lastScanTime.current = 0;

      await codeReader.current.decodeFromVideoDevice(
        null,
        videoRef.current,
        (result, error) => {
          const now = Date.now();
          if (result && now - lastScanTime.current > 1500) {
            lastScanTime.current = now;
            const scannedId = result.getText().trim();

            setSelectedOrders((prev) => {
              if (prev.includes(scannedId)) {
                console.warn(`Item "${scannedId}" already scanned.`);
                return prev;
              }
              playBeep();
              setOrderIdToFetch(scannedId); // trigger details fetch
              return [...prev, scannedId];
            });
          }
          if (error && !(error instanceof NotFoundException)) {
            console.error("Scanning error:", error);
          }
        }
      );
    } catch (error) {
      console.error("Scanner failed:", error);
      alert("Scanner error. Check camera permissions.");
      stopScanner();
    }
  }, [mode, stopScanner]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  const toggleScanner = () => {
    if (!mode) {
      alert("Please select Warehouse, Route, Driver and set Capacity first.");
      return;
    }
    if (isScanning) stopScanner();
    else startScanner();
  };

  // stop scan when leaving scan mode
  useEffect(() => {
    if (mode !== "scan" && isScanning) {
      stopScanner();
    }
  }, [mode, isScanning, stopScanner]);

  return (
    <div className="w-full py-4 px-6 space-y-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]  ">
      <div className=" h-[calc(100vh-13rem)] overflow-y-auto space-y-4">
        <div className="">
          <NewSelectInput
            name="preferredMethod"
            label="Preferred Method"
            className="col-span-2"
            placeholder="Select Method"
            selectOptions={[
              { id: "automatic", value: "automatic", text: "Automatic" },
              { id: "scan", value: "scan", text: "Scan & Paste" }, 
            ]}
            value={mode}
            setValue={(val: string) => {
              setMode(val);
            }}
          />
        </div>
        <div className="flex justify-between items-center">
          <h2 className="text-[#212121] text-base font-medium">
            Shipments list (
            {mode === "scan"
              ? selectedOrders.length
              : orders?.orders?.length || 0}
            )
          </h2>
           
        </div>

      
        {mode === "automatic" && (
          <ProductTable
            orders={orders?.orders}
            selectedOrders={selectedOrders}
            setSelectedOrders={setSelectedOrders}
            // pageNo={pageNo}
            // limit={limit}
          />
        )}

        {mode === "scan" && (
          <div className="space-y-4">
            <textarea
              value={pasteData}
              onChange={(e) => {
                const value = e.target.value;
                setPasteData(value);
                const parsed = value
                  .split(/[\n,\t]+/)
                  .map((o) => o.trim())
                  .filter((o) => o.length > 0);

                const deduped = Array.from(new Set(parsed));
                setSelectedOrders(deduped);
              }}
              placeholder="Paste order IDs here, separated by new lines, commas, or tabs..."
              className="w-full border outline-none rounded p-2 text-sm resize-none"
              rows={6}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddShipmentIdWise;
